import { createContext, useContext, useEffect, useState } from 'react';
import { Amplify } from 'aws-amplify';
import { getCurrentUser, signOut, fetchUserAttributes } from 'aws-amplify/auth';
import { amplifyConfig, isAuthEnabled, validateAuthConfig } from './amplify-config';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authConfigured, setAuthConfigured] = useState(false);

  useEffect(() => {
    // Configure Amplify if auth is enabled
    if (isAuthEnabled) {
      const isValid = validateAuthConfig();
      if (isValid) {
        Amplify.configure(amplifyConfig);
        setAuthConfigured(true);
        checkAuthState();
      } else {
        setLoading(false);
      }
    } else {
      // Mock user for development
      setUser({ email: 'dev@example.com', sub: 'dev-user' });
      setLoading(false);
    }
  }, []);

  const checkAuthState = async () => {
    try {
      const currentUser = await getCurrentUser();
      
      // Try to fetch user attributes for display name
      let userAttributes = {};
      try {
        userAttributes = await fetchUserAttributes();
      } catch (attrError) {
        console.log('Could not fetch user attributes:', attrError);
      }
      
      // Extract clean username (remove provider prefix like "Midway_")
      const rawUsername = currentUser.username || '';
      const cleanUsername = rawUsername.includes('_') 
        ? rawUsername.split('_').slice(1).join('_')  // Get everything after first underscore
        : rawUsername;
      
      // Combine user info with attributes
      setUser({
        ...currentUser,
        attributes: userAttributes,
        // Helper properties for easy access
        displayName: userAttributes.given_name || 
                     userAttributes.preferred_username || 
                     userAttributes.email?.split('@')[0] ||
                     cleanUsername ||
                     'User',
        email: userAttributes.email || currentUser.signInDetails?.loginId,
        firstName: userAttributes.given_name,
        lastName: userAttributes.family_name,
        cleanUsername: cleanUsername
      });
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    if (!authConfigured) return;
    
    try {
      // Local sign out only - clears Cognito tokens but doesn't sign out from Midway
      await signOut({ global: false });
      setUser(null);
      
      // Redirect to homepage
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
      // Even if signOut fails, clear local state and redirect
      setUser(null);
      window.location.href = '/';
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    isAuthEnabled,
    authConfigured,
    logout,
    refreshAuth: checkAuthState
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
